import type { RoasterApprovalRequestDraft } from "@shared/services/salesforce/types";
import * as T from "@shared/types";

import i18n from "@client-shared/lib/i18n";
import { getBrowserTimeZone } from "@client-shared/lib/timeZone";
import { createIndividualVariantProducts } from "@client-shared/lib/util";
import * as api from "@client-shared/services/api";
import React from "react";
import { handleDisplayErrorData } from "../../services/api/utilities/handleDisplayErrorData";
import { useConfigState } from "../config/useConfigState";

import { setSingleParentProducts } from "@client-shared/utilities/productSyncUtilities";
import type { SalesforceProduct } from "@shared/index";
import { useSFDCAuthState } from "../auth/useSFDCAuthState";
import useVendorContext from "../vendor/useCurrentVendor";
import {
  addSkuImportDraft,
  editSkuImportDrafts,
  enableVendorScopedDrafts,
  reducer,
  setAllRemoteFormProducts,
  setApprovalRequests,
  setConnections,
  setError,
  setFilteredConnections,
  setFilteredLocalProducts,
  setFilteredRemoteProducts,
  setInitialized,
  setLoading,
  setLocalParentChildProductsAction,
  setLocalProducts,
  setRemoteFormProductsWithImports,
  setRemoteProducts,
  setServiceProducts,
  setSkuImportDrafts,
} from "./actions";
import { ProductsActions, ProductsContextState, ProductsState } from "./types";
import { mapAllProducts } from "./utilities/productDataTransformations";
import { roundApprovalRequest, roundSalesforceProduct } from "./utilities/productPayloadRounding";

export const initialState: ProductsState = {
  initialized: false,
  installationId: undefined,
  vendorScopedDraftsEnabled: false,
  remoteProducts: undefined,
  remoteFormProducts: undefined,
  remoteFormProductsWithImported: undefined,
  skuImportDrafts: undefined,
  filteredRemoteProducts: undefined,
  localProducts: undefined,
  filteredLocalProducts: undefined,
  serviceProducts: undefined,
  connections: undefined,
  filteredConnections: undefined,
  localParentChildProducts: undefined,
  approvalRequests: undefined,
  loading: {
    remoteProducts: true,
    localProducts: true,
    serviceProducts: true,
    connections: true,
    skuImportDrafts: true,
    approvalRequests: true,
  },
  error: {
    remoteProducts: undefined,
    localProducts: undefined,
    serviceProducts: undefined,
    connections: undefined,
    skuImportDrafts: undefined,
    approvalRequests: undefined,
  },
};

export const defaultContextState: ProductsContextState = [initialState, () => undefined];

export const ProductsContext = React.createContext(defaultContextState);

type Props = {
  children: React.ReactNode;
};

export const ProductsProvider: React.FC<Props> = ({ children }: Props) => {
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const [authState] = useSFDCAuthState();
  const [{ initialized: isVendorInitialized, currentVendor }] = useVendorContext();
  const [currentInstallationId, setCurrentInstallationId] = React.useState<string | undefined>(undefined);
  // Read by the "get the remote products" effect below when it re-fires (the
  // effect is the sole place that calls fetchRemoteProducts — a ref, not
  // state, so setting it never itself triggers a render/re-fetch).
  const remoteProductsSearchParamsRef = React.useRef<{ title?: string; productIds?: string } | undefined>(undefined);
  // initialize the context

  const [{ config }, ,] = useConfigState();
  const {
    productConfig,
    allBagSizesConfig,
    allGrindTypesConfig,
    defaultRegionLocalisedProductConfig,
    selectedLocalisedProductConfig,
    regionLargeBagSize,
    roasterNoteMaxLength,
    titleMaxLength,
  } = config;

  React.useEffect(() => {
    if (!isVendorInitialized || !authState || state.initialized || !currentInstallationId) {
      return;
    }
    if (!state.initialized) {
      dispatch(setInitialized(true));
    }
  }, [authState, currentInstallationId, isVendorInitialized, state.initialized]);

  // get the connections
  React.useEffect(() => {
    if (!state.initialized || !currentInstallationId || !state.loading.connections) {
      return;
    }
    void fetchConnections(currentInstallationId, dispatch);
  }, [currentInstallationId, state.initialized, state.loading.connections]);

  // set the filtered connections
  React.useEffect(() => {
    if (!state.initialized || !state.connections || state.filteredConnections) {
      return;
    }
    dispatch(setFilteredConnections(transformToFilteredConnections(state.connections)));
  }, [state.connections, state.initialized, state.filteredConnections]);

  // get the local products
  React.useEffect(() => {
    if (!state.initialized || !currentVendor?.vendorKey || !state.loading.localProducts) {
      return;
    }
    void fetchLocalProducts(currentVendor, dispatch);
  }, [currentVendor, currentVendor?.vendorKey, state.initialized, state.loading.localProducts]);

  // set the filtered local products
  React.useEffect(() => {
    if (!state.initialized || !state.filteredConnections || !state.localProducts || state.filteredLocalProducts) {
      return;
    }
    dispatch(setFilteredLocalProducts(transformToFilteredLocalProducts(state.filteredConnections, state.localProducts)));
  }, [state.filteredConnections, state.initialized, state.localProducts, state.filteredLocalProducts]);

  // get the remote products — the sole caller of fetchRemoteProducts, so a
  // search-triggered setLoading("remoteProducts", true) (handleSearchRemoteProducts
  // below) re-fires this same effect rather than racing a second, separate fetch.
  React.useEffect(() => {
    if (!state.initialized || !currentVendor?.vendorKey || !currentInstallationId || !state.loading.remoteProducts) {
      return;
    }
    const searchParams = remoteProductsSearchParamsRef.current;
    remoteProductsSearchParamsRef.current = undefined;
    void fetchRemoteProducts(currentInstallationId, currentVendor, dispatch, searchParams);
  }, [currentVendor, currentVendor?.vendorKey, state.initialized, currentInstallationId, state.loading.remoteProducts]);

  // get the filtered remote products
  React.useEffect(() => {
    if (!state.initialized || !state.filteredConnections || !state.remoteProducts || state.filteredRemoteProducts) {
      return;
    }
    dispatch(setFilteredRemoteProducts(transformToFilteredProducts(state.filteredConnections, state.remoteProducts)));
  }, [state.filteredConnections, state.initialized, state.remoteProducts, state.filteredRemoteProducts]);

  // get the sku import drafts. Scoped by installationId when one exists
  // (Shopify-installation flow, unchanged — still gated on `state.initialized`,
  // which itself only flips once an installationId is set). For vendors with
  // no installation (e.g. ShipStation roasters), `state.initialized` never
  // flips, so `handleEnableVendorScopedDrafts` acts as this effect's own
  // alternate readiness signal — bypassing both the installationId and the
  // initialized gate for this fetch only, without touching the broader
  // initialization flag or any other fetch effect.
  React.useEffect(() => {
    if (!state.loading.skuImportDrafts) {
      return;
    }
    if (state.vendorScopedDraftsEnabled) {
      void fetchSkuImportDrafts(currentInstallationId, dispatch);
      return;
    }
    if (!state.initialized || !currentInstallationId) {
      return;
    }
    void fetchSkuImportDrafts(currentInstallationId, dispatch);
  }, [currentInstallationId, state.initialized, state.loading.skuImportDrafts, state.vendorScopedDraftsEnabled]);

  // get the service products
  React.useEffect(() => {
    if (!state.initialized || !currentVendor?.vendorKey || !state.loading.serviceProducts) {
      return;
    }

    void fetchServiceProducts(currentVendor, dispatch);
  }, [authState, currentVendor, currentVendor?.vendorKey, state.initialized, state.loading.serviceProducts]);

  // fetch approval requests from Salesforce
  React.useEffect(() => {
    if (!state.initialized || !currentVendor?.vendorKey || !state.loading.approvalRequests) return;
    void fetchApprovalRequests(dispatch);
  }, [currentVendor?.vendorKey, state.initialized, state.loading.approvalRequests]);

  const handleSetAllRemoteFormProducts = React.useCallback(
    (hasImports: boolean) => {
      if (
        !state.filteredRemoteProducts ||
        !state.remoteProducts ||
        !productConfig ||
        !selectedLocalisedProductConfig ||
        !defaultRegionLocalisedProductConfig ||
        !state.localProducts ||
        !state.filteredConnections
      ) {
        return [];
      }

      // if hasImports then use imports and normal data
      // if not use filtered data
      return mapAllProducts({
        products: hasImports ? state.remoteProducts : state.filteredRemoteProducts,
        productConfig,
        importedProducts: hasImports ? state.serviceProducts : undefined,
        localProducts: state.localProducts,
        connections: hasImports ? state.connections : state.filteredConnections,
        allBagSizesConfig,
        allGrindTypesConfig,
        localisedProductConfig: defaultRegionLocalisedProductConfig,
        roasterNoteMaxLength,
        titleMaxLength,
        regionLargeBagSize,
      });
    },
    [
      state.filteredRemoteProducts,
      state.filteredConnections,
      state.remoteProducts,
      state.serviceProducts,
      state.localProducts,
      state.connections,
      productConfig,
      selectedLocalisedProductConfig,
      defaultRegionLocalisedProductConfig,
      allBagSizesConfig,
      allGrindTypesConfig,
      roasterNoteMaxLength,
      titleMaxLength,
      regionLargeBagSize,
    ]
  );
  React.useEffect(() => {
    if (
      !state.filteredRemoteProducts ||
      !productConfig ||
      !selectedLocalisedProductConfig ||
      !defaultRegionLocalisedProductConfig ||
      !state.localProducts ||
      !state.filteredConnections
    ) {
      return;
    }
    const remoteFormProductsWithImports = handleSetAllRemoteFormProducts(true);
    dispatch(setRemoteFormProductsWithImports(remoteFormProductsWithImports));
    const remoteFormProducts = handleSetAllRemoteFormProducts(false);
    dispatch(setAllRemoteFormProducts(remoteFormProducts));
  }, [
    handleSetAllRemoteFormProducts,
    state.filteredRemoteProducts,
    productConfig,
    selectedLocalisedProductConfig,
    state.localProducts,
    state.filteredConnections,
    state.loading.remoteProducts,
    state.loading.localProducts,
    state.loading.serviceProducts,
    state.loading.connections,
    defaultRegionLocalisedProductConfig,
  ]);

  React.useEffect(() => {
    if (!state.localProducts || state.localProducts.length === 0) {
      return;
    }
    const localParentChildProducts = setSingleParentProducts(state.localProducts);
    if (!localParentChildProducts || localParentChildProducts.length === 0) {
      return;
    }
    dispatch(setLocalParentChildProductsAction(localParentChildProducts));
  }, [state.localProducts]);

  const handleSetInstallationId = React.useCallback(
    (installationId: string) => {
      if (currentInstallationId === installationId) {
        return;
      }
      setCurrentInstallationId(installationId);
    },
    [currentInstallationId]
  );

  const [updatingSkuStatusCount, setUpdatingSkuStatusCount] = React.useState(0);
  const isUpdatingSkuStatus = updatingSkuStatusCount > 0;

  const handleUpdateSkuStatus = React.useCallback(
    async (requests: RoasterApprovalRequestDraft[]) => {
      const requestorId = currentVendor?.userId;
      const accessToken = authState?.sfdcAuthToken;
      if (!requestorId) {
        throw new Error("Cannot submit approval request: missing requestorId (currentVendor.userId)");
      }
      if (!accessToken) {
        throw new Error("Cannot submit approval request: missing Salesforce access token");
      }
      // Lets the backend validate and resolve "today" in the roaster's zone for the Visible for Sale -> Out of Stock lead time rule.
      const roasterTimeZone = getBrowserTimeZone();
      setUpdatingSkuStatusCount((c) => c + 1);
      try {
        const results = await api.postApprovalRequests(
          accessToken,
          requests.map((request) => roundApprovalRequest({ ...request, roasterTimeZone, requestorId }))
        );
        await fetchApprovalRequests(dispatch);
        return results;
      } finally {
        setUpdatingSkuStatusCount((c) => c - 1);
      }
    },
    [authState?.sfdcAuthToken, currentVendor?.userId]
  );

  const [updatingWeeklyLimitCount, setUpdatingWeeklyLimitCount] = React.useState(0);
  const isUpdatingWeeklyLimit = updatingWeeklyLimitCount > 0;

  const handleUpdateWeeklyLimit = React.useCallback(
    async (request: RoasterApprovalRequestDraft) => {
      const requestorId = currentVendor?.userId;
      const accessToken = authState?.sfdcAuthToken;
      if (!requestorId) {
        throw new Error("Cannot submit approval request: missing requestorId (currentVendor.userId)");
      }
      if (!accessToken) {
        throw new Error("Cannot submit approval request: missing Salesforce access token");
      }
      setUpdatingWeeklyLimitCount((c) => c + 1);
      try {
        const results = await api.postApprovalRequests(accessToken, [roundApprovalRequest({ ...request, requestorId })]);
        await fetchApprovalRequests(dispatch);
        return results;
      } finally {
        setUpdatingWeeklyLimitCount((c) => c - 1);
      }
    },
    [authState?.sfdcAuthToken, currentVendor?.userId]
  );

  ///////////////////////////////////////////////////////////////
  // utilities
  //

  const handlePostSkuImportDraft = React.useCallback(
    async (product: T.FormProduct) => {
      if (!currentVendor) {
        return;
      }
      const newDraft = await postSkuImportDraft(product, currentVendor, currentInstallationId);
      if (!newDraft) {
        return;
      }
      dispatch(addSkuImportDraft(newDraft));
    },
    [currentInstallationId, currentVendor]
  );

  const handleUpdateSkuImportDrafts = React.useCallback(
    async (products: T.FormProduct[], status: T.SkuImportDraftState): Promise<boolean> => {
      if (!currentVendor) {
        return false;
      }

      const newDrafts = await updateSkuImportDrafts(products, currentVendor, currentInstallationId, status); //
      if (!newDrafts) {
        return false;
      }
      dispatch(editSkuImportDrafts(newDrafts, status));
      return true;
    },
    [currentInstallationId, currentVendor]
  );

  const handleEnableVendorScopedDrafts = React.useCallback(() => {
    dispatch(enableVendorScopedDrafts());
  }, []);

  const handleSyncProductConnections = React.useCallback(
    async (connectionParams: T.CreateConnectionParams[], updatedConnections: T.ProductConnection[]): Promise<boolean> => {
      try {
        let hasSubmitted = false;
        if (connectionParams.length > 0) {
          await api.postProductConnections(connectionParams);
          hasSubmitted = true;
        }
        if (updatedConnections.length > 0) {
          await api.putProductConnections(updatedConnections);
          hasSubmitted = true;
        }

        if (hasSubmitted && currentInstallationId) {
          dispatch(setLoading("connections", true));
          await fetchConnections(currentInstallationId, dispatch);
        }
        return hasSubmitted;
      } catch (e) {
        dispatch(setError("connections", e as Error));
        return false;
      }
    },
    [currentInstallationId]
  );

  // Re-fetches remote products, optionally scoped to a search term. Called
  // with no args to restore the full, unfiltered catalog (e.g. clearing a
  // search or closing the search UI). Stashes searchParams in a ref and
  // dispatches setLoading — the "get the remote products" effect above is the
  // one place that actually calls fetchRemoteProducts, so this never races a
  // second, separately-triggered fetch.
  const handleSearchRemoteProducts = React.useCallback(
    (searchParams?: { title?: string; productIds?: string }) => {
      if (!currentVendor || !currentInstallationId) {
        return;
      }
      remoteProductsSearchParamsRef.current = searchParams;
      dispatch(setLoading("remoteProducts", true));
    },
    [currentVendor, currentInstallationId]
  );

  // context value
  const contextValue: ProductsContextState = React.useMemo(
    () => [
      state,
      dispatch,
      {
        handleInstallationId: handleSetInstallationId,
        handleEnableVendorScopedDrafts: handleEnableVendorScopedDrafts,
        handlePostSkuImportDraft: handlePostSkuImportDraft,
        handleUpdateSkuImportDrafts: handleUpdateSkuImportDrafts,
        handlePostProducts: postProducts,
        handleUpdateSkuStatus: handleUpdateSkuStatus,
        isUpdatingSkuStatus: isUpdatingSkuStatus,
        handleUpdateWeeklyLimit: handleUpdateWeeklyLimit,
        isUpdatingWeeklyLimit: isUpdatingWeeklyLimit,
        handleSyncProductConnections: handleSyncProductConnections,
        handleSearchRemoteProducts: handleSearchRemoteProducts,
      },
    ],
    [
      state,
      handleSetInstallationId,
      handleEnableVendorScopedDrafts,
      handlePostSkuImportDraft,
      handleUpdateSkuImportDrafts,
      handleUpdateSkuStatus,
      isUpdatingSkuStatus,
      handleUpdateWeeklyLimit,
      isUpdatingWeeklyLimit,
      handleSyncProductConnections,
      handleSearchRemoteProducts,
    ]
  );
  return <ProductsContext.Provider value={contextValue}>{children}</ProductsContext.Provider>;
};

//sideEffects
const fetchConnections = async (installationId: string, dispatch: (action: ProductsActions) => void) => {
  try {
    const variantConnections = await api.getInstallationProductConnections(installationId);
    if (!variantConnections) {
      return;
    }
    dispatch(setConnections(variantConnections));
    const filteredVariantConnections = variantConnections.filter((v) => v.state !== "DELETED" && v.state !== "PAUSED");
    dispatch(setFilteredConnections(filteredVariantConnections));
  } catch (e) {
    handleDisplayErrorData(
      i18n.t("apiErrorMessages.fetchConnections.message"),
      i18n.t("apiErrorMessages.fetchConnections.title"),
      e as Error
    );
    dispatch(setError("connections", e as Error));
  } finally {
    dispatch(setLoading("connections", false));
  }
};

const fetchRemoteProducts = async (
  installationId: string,
  currentVendor: T.Vendor,
  dispatch: (action: ProductsActions) => void,
  searchParams?: { title?: string; productIds?: string }
) => {
  try {
    const remoteProducts = await api.getInstallationProducts(currentVendor, installationId, searchParams);
    if (!remoteProducts) {
      return;
    }
    dispatch(setRemoteProducts(remoteProducts));
  } catch (e) {
    handleDisplayErrorData(
      i18n.t("apiErrorMessages.fetchRemoteProducts.message"),
      i18n.t("apiErrorMessages.fetchRemoteProducts.title"),
      e as Error
    );
    dispatch(setError("remoteProducts", e as Error));
  } finally {
    dispatch(setLoading("remoteProducts", false));
  }
};

const fetchSkuImportDrafts = async (installationId: string | undefined, dispatch: (action: ProductsActions) => void) => {
  try {
    const skuImportDrafts = await api.getSkuImportDrafts(installationId);
    if (!skuImportDrafts) {
      return [];
    }
    dispatch(setSkuImportDrafts(skuImportDrafts?.map((draft) => draft.draftFormProduct)));
  } catch (e) {
    handleDisplayErrorData(
      i18n.t("apiErrorMessages.fetchSkuImportProducts.message"),
      i18n.t("apiErrorMessages.fetchSkuImportProducts.title"),
      e as Error
    );
    dispatch(setError("skuImportDrafts", e as Error));
  } finally {
    dispatch(setLoading("skuImportDrafts", false));
  }
};

const postSkuImportDraft = async (product: T.FormProduct, vendor: T.Vendor, installationId: string | undefined) => {
  try {
    const response = await api.postSkuImportDrafts(vendor, installationId, [product]);
    if (!response) {
      // The API silently skips persistence when the vendor has no contactId —
      // surface it instead of optimistically adding a draft to state that
      // would vanish on the next reload.
      handleDisplayErrorData("draft could not be saved", "vendor has no contact id");
      return undefined;
    }
    return product;
  } catch (e) {
    handleDisplayErrorData("error with posting draft", "someError in drafts", e as Error);
  }
};

const updatedSingleSkuImportDraft = async (
  product: T.FormProduct,
  vendor: T.Vendor,
  installationId: string | undefined,
  status: T.SkuImportDraftState
) => {
  const response = await api.patchSkuImportDrafts(vendor, installationId, product, status);
  if (!response) {
    // The API silently skips the update when the vendor has no contactId —
    // throw so updateSkuImportDrafts' catch surfaces it instead of the state
    // change being applied without ever reaching the backend.
    throw new Error("draft update skipped: vendor has no contact id");
  }
  return { ...product, draftState: status } as T.FormProduct;
};

const updateSkuImportDrafts = async (
  products: T.FormProduct[],
  vendor: T.Vendor,
  installationId: string | undefined,
  status: T.SkuImportDraftState
) => {
  try {
    await Promise.all(products.map((product) => updatedSingleSkuImportDraft(product, vendor, installationId, status)));
    return products;
  } catch (e) {
    handleDisplayErrorData("error with updating drafts", "someError in drafts", e as Error);
  }
};

const fetchLocalProducts = async (currentVendor: T.Vendor, dispatch: (action: ProductsActions) => void) => {
  try {
    const localProducts = await api.getServiceProducts(currentVendor, "pim");
    if (!localProducts) {
      return;
    }
    dispatch(setLocalProducts(createIndividualVariantProducts(localProducts)));
  } catch (e) {
    handleDisplayErrorData(
      i18n.t("apiErrorMessages.fetchLocalProducts.message"),
      i18n.t("apiErrorMessages.fetchLocalProducts.title"),
      e as Error
    );
    dispatch(setError("localProducts", e as Error));
    dispatch(setLocalProducts([]));
  } finally {
    dispatch(setLoading("localProducts", false));
  }
};

const fetchServiceProducts = async (currentVendor: T.Vendor, dispatch: (action: ProductsActions) => void) => {
  try {
    const serviceProducts = await api.getServiceProducts(currentVendor, "salesforce");
    if (!serviceProducts) {
      return;
    }
    dispatch(setServiceProducts([...new Set(serviceProducts)]));
  } catch (e) {
    handleDisplayErrorData(
      i18n.t("apiErrorMessages.fetchServiceProducts.message"),
      i18n.t("apiErrorMessages.fetchServiceProducts.title"),
      e as Error
    );
    dispatch(setError("serviceProducts", e as Error));
  } finally {
    dispatch(setLoading("serviceProducts", false));
  }
};

const fetchApprovalRequests = async (dispatch: (action: ProductsActions) => void) => {
  try {
    const approvalRequests = await api.getApprovalRequests();
    dispatch(setApprovalRequests(approvalRequests ?? []));
  } catch (e) {
    handleDisplayErrorData(
      i18n.t("apiErrorMessages.fetchApprovalRequests.message"),
      i18n.t("apiErrorMessages.fetchApprovalRequests.title"),
      e as Error
    );
    dispatch(setError("approvalRequests", e as Error));
  } finally {
    dispatch(setLoading("approvalRequests", false));
  }
};

// data transformation

export const transformToFilteredConnections = (connections: T.ProductConnection[]) => {
  return connections.filter((v) => v.state !== "DELETED" && v.state !== "PAUSED");
};

export const transformToFilteredLocalProducts = (filteredConnections: T.ProductConnection[], localProducts: T.Product[]) => {
  const connectedLocalVariantIds = new Set(filteredConnections.map((connection) => connection.sourceObjectId));
  const individualVariantProducts = localProducts ? createIndividualVariantProducts(localProducts) : [];
  const filteredLocalProducts = individualVariantProducts.map((product) => ({
    ...product,
    variants: product.variants?.filter((variant) => !connectedLocalVariantIds.has(variant.id)),
  }));
  return filteredLocalProducts;
};

export const transformToFilteredProducts = (filteredConnections: T.ProductConnection[], remoteProducts: T.Product[]) => {
  const connectedRemoteVariantIds = new Set(filteredConnections.map((connection) => connection.targetVariantId));
  const filteredRemoteProducts = remoteProducts.map((product) => ({
    ...product,
    variants: product.variants?.filter((variant) => !connectedRemoteVariantIds.has(variant.id)),
  }));
  return filteredRemoteProducts;
};

export const postProducts = async (serviceId: string, accessToken: string, products: Partial<SalesforceProduct>[]) => {
  await api.postProducts(serviceId, accessToken, products.map(roundSalesforceProduct));
};
